import {
  ClientAdministrationDetail,
  ClientDeviceCounts,
  ClientLicenceSummary,
  ClientTargetVersionField,
  CLIENT_TARGET_VERSION_FIELDS,
  ClientTestSettings,
  ClientTestUser,
  ClientGroup,
  ClientGroupClient,
  ClientProducts,
  ClientProjectOrganisation,
  ClientSystemConfiguration,
  ClientProjectTypes,
  ContractType,
  DEVICE_COUNT_ASSIGNMENT_TYPES,
  DeviceCountAssignmentType,
} from "@/data/models/ClientAdministrationRow";
import { liveOperationValue } from "@/utils/ClientLiveOperation";
import { derivedProductChoice, productChoiceLabelKey } from "@/utils/ClientProducts";
import { PROJECT_ORGANISATION_QUESTIONS, projectOrganisationText } from "@/utils/ClientProjectOrganisation";
import { SYSTEM_CONFIGURATION_ROWS, rowText } from "@/utils/ClientSystemConfiguration";
import { projectTypeText } from "@/utils/ClientProjectTypes";
import { deleteReasonLabel } from "@/utils/ClientStatus";
import { formatDateToDayMonthYear } from "@/utils/DateHelper";

/** Shown for any value that is absent or blank, as the Android detail screen shows "-". */
export const EMPTY_VALUE = "–";

/**
 * A target app file as the IRES Admin Client shows it: only its build number, "2011" for
 * "HyDry-Firebase-2011.apk" (Taimoor, 2026-10-14: every stored value has that form, so the prefix
 * repeated on every row). A value of any other form is shown as stored, never dropped; absent is "–".
 */
export function targetAppBuildText(targetAppApk: string | null | undefined): string {
  if (!targetAppApk || targetAppApk.trim() === "") {
    return EMPTY_VALUE;
  }
  const build = /-(\d+)\.apk$/i.exec(targetAppApk.trim());
  return build ? build[1] : targetAppApk;
}

/**
 * The portal screens a detail value opens. `archivedProjects` (Alte and Gelöschte together) is what
 * the archive count opens when an older IRES Cloud serves no split figures.
 */
export type ClientDetailLink =
  | "projects"
  | "archivedProjects"
  | "oldProjects"
  | "deletedProjects"
  | "reports"
  | "users";

/** One labelled value on the detail screen. */
export interface ClientDetailField {
  labelKey: string;
  value: string;
  /** The portal screen this value opens, as the Geräte card's numbers open the device list. */
  link?: ClientDetailLink;
  /**
   * A value that can be changed here: the client's own Beschreibung target, "Kein Echtbetrieb", or a
   * Status flag (Gesperrt, Aktiv; #2610067).
   */
  action?: "infoConfigTarget" | "liveOperation" | "miraAllClients" | "blocked" | "active";
  /** Mandanten shown as links to their own detail: a group's members, a member's master (#2610074). */
  clients?: ClientGroupClient[];
  /** A label that is not one i18n key, shown instead of `labelKey`: a project type's "LO Leckortung" (#2610081). */
  label?: string;
  /** A grey line under the value: whether a project type is on in the client's own table (#2610081). */
  caption?: string;
}

/** One card of the detail screen. */
export interface ClientDetailSection {
  titleKey: string;
  fields: ClientDetailField[];
  /** A body line above the fields explaining the card (the Hilfsfunktionen card, #2610077). */
  descriptionKey?: string;
  /** A card that can be edited from its title (backlog #38, #2610070), when IRES Cloud serves its values. */
  edit?: "masterData" | "contracts" | "testSettings" | "group" | "products" | "projectTypes" | "projectOrganisation" | "systemConfiguration";
  /** The Hilfsfunktionen card's one action in its title (#2610077): recalculate every drying. */
  helperAction?: "recalculateAllDryings";
  /** The Gruppe card's "Geteilte Benutzer migrieren" in its title, on a MASTER-Mandant (#2610074). */
  groupAction?: "migrateSharedUsers";
  /** A warning under the card's values: the Test card's test that reaches nobody (#2610070). */
  noteKey?: string;
  /**
   * A grey caption under the card's values: the Produkte card's "derived, not stored yet" line (#2610089),
   * the Projekttypen card's "not restricted" (#2610081).
   */
  captionKey?: string;
  /** The Status card's LÖSCHEN or WIEDERHERSTELLEN in its title (#2610067), when IRES Cloud serves the Status. */
  statusAction?: "delete" | "restore";
}

type Translate = (key: string, values?: unknown[]) => string;

const PREFIX = "admin.clients.detail";

function text(value: string | null | undefined): string {
  return value && value.trim() !== "" ? value : EMPTY_VALUE;
}

function yesNo(value: boolean | null, t: Translate): string {
  if (value === null) {
    return EMPTY_VALUE;
  }

  return t(value ? "common.yes" : "common.no");
}

function count(value: number | null, formatCount: (value: number) => string): string {
  return value === null ? EMPTY_VALUE : formatCount(value);
}

/** IRES, Kunde, Sonstige; an unknown stored value is shown as it is rather than hidden. */
function clientTypeLabel(value: string | null, t: Translate): string {
  switch (value) {
    case "IRES":
      return t(`${PREFIX}.clientTypes.IRES`);
    case "CLIENT":
      return t(`${PREFIX}.clientTypes.CLIENT`);
    case "OTHER":
      return t(`${PREFIX}.clientTypes.OTHER`);
    default:
      return text(value);
  }
}

const CONTRACT_TYPE_ORDER: ContractType[] = [
  ContractType.PURCHASE,
  ContractType.PERMANENT_RENT,
  ContractType.TESTING,
];

function contractsLabel(values: ContractType[], t: Translate): string {
  const labels = CONTRACT_TYPE_ORDER.filter((type) => values.includes(type)).map((type) =>
    t(`${PREFIX}.contractTypes.${type}`)
  );

  return labels.length > 0 ? labels.join(", ") : EMPTY_VALUE;
}

/**
 * Projekte, Alte and Gelöschte Projekte, Berichte, and when they were counted: the same figures as the
 * client's row on the Mandanten list (ticket 2610024). An IRES Cloud build from before the figures
 * serves only the stored counters, which count the archive as one figure; that is shown as before.
 */
function projectAndReportFields(
  detail: ClientAdministrationDetail,
  formatCount: (value: number) => string,
  figuresUpdatedAtLabel: string | null
): ClientDetailField[] {
  const figures = detail.figures;

  if (!figures) {
    return [
      { labelKey: `${PREFIX}.fields.numProjects`, value: count(detail.numProjects, formatCount), link: "projects" },
      // Archived and deleted together, as the counter counts them; opens with Alte and Gelöschte ticked.
      {
        labelKey: `${PREFIX}.fields.numArchivedProjects`,
        value: count(detail.numArchivedProjects, formatCount),
        link: "archivedProjects",
      },
      { labelKey: `${PREFIX}.fields.numReports`, value: count(detail.numReports, formatCount), link: "reports" },
    ];
  }

  return [
    { labelKey: `${PREFIX}.fields.numProjects`, value: count(figures.numProjects, formatCount), link: "projects" },
    { labelKey: `${PREFIX}.fields.numArchivedProjects`, value: count(figures.numOldProjects, formatCount), link: "oldProjects" },
    {
      labelKey: `${PREFIX}.fields.numDeletedProjects`,
      value: count(figures.numDeletedProjects, formatCount),
      link: "deletedProjects",
    },
    { labelKey: `${PREFIX}.fields.numReports`, value: count(figures.numReports, formatCount), link: "reports" },
    ...(figuresUpdatedAtLabel
      ? [{ labelKey: `${PREFIX}.fields.figuresUpdatedAt`, value: figuresUpdatedAtLabel }]
      : []),
  ];
}

/** A contract's Ja with its start date, "Ja (ab 01.01.2025)", when one is recorded (backlog #38, slice 2). */
function withSince(value: string, sinceMillis: number | null | undefined, t: Translate): string {
  const date = typeof sinceMillis === "number" ? formatDateToDayMonthYear(new Date(sinceMillis)) : null;
  return date ? `${value} (${t("admin.clients.serviceContractSince", [date])})` : value;
}

/**
 * Gesperrt and Aktiv, each changeable when IRES Cloud serves the Status (#2610067), and Gelöscht with
 * its reason while the client is deleted. An older IRES Cloud gets the read-only Gesperrt it had.
 */
function statusFields(detail: ClientAdministrationDetail, t: Translate): ClientDetailField[] {
  if (!detail.status) {
    return [{ labelKey: `${PREFIX}.fields.blocked`, value: yesNo(detail.blocked, t) }];
  }

  const { status, statusChanges } = detail.status;
  const deletedReason = statusChanges.deleted?.reason;
  return [
    { labelKey: `${PREFIX}.fields.blocked`, value: yesNo(status.blocked, t), action: "blocked" },
    { labelKey: `${PREFIX}.fields.isActive`, value: yesNo(status.isActive, t), action: "active" },
    ...(status.deleted
      ? [
          {
            labelKey: `${PREFIX}.fields.deleted`,
            value: deletedReason ? `${yesNo(true, t)} (${deleteReasonLabel(deletedReason, t)})` : yesNo(true, t),
          },
        ]
      : []),
  ];
}

/**
 * Whether a test is set that reaches nobody (#2610070, D3): a test app or Beschreibung target with no
 * active test user. IRES Project and IRES Cloud give the targets to the test users only.
 */
export function testReachesNobody(settings: ClientTestSettings): boolean {
  const hasTarget = settings.appVersion !== null || settings.roomInfoConfigVersion !== null;
  return hasTarget && !settings.testUsers.some((user) => user.active);
}

/** The test users by name, a deleted or missing one marked; "–" for none. */
export function testUsersText(users: ClientTestUser[], t: Translate): string {
  if (users.length === 0) {
    return EMPTY_VALUE;
  }
  return users
    .map((user) => {
      const name = user.name ?? t(`${PREFIX}.testSettings.unknownUser`);
      return user.active ? name : `${name} ${t(`${PREFIX}.testSettings.inactiveUser`)}`;
    })
    .join(", ");
}

/** The Test card (#2610070): the ERP-Simulator and the Test-App targets, editable from its title. */
function testSettingsSection(settings: ClientTestSettings, t: Translate): ClientDetailSection {
  return {
    titleKey: `${PREFIX}.sections.testSettings`,
    edit: "testSettings",
    ...(testReachesNobody(settings) ? { noteKey: `${PREFIX}.testSettings.reachesNobody` } : {}),
    fields: [
      { labelKey: `${PREFIX}.fields.isTestSystem`, value: yesNo(settings.isTestSystem, t) },
      { labelKey: `${PREFIX}.fields.testAppVersion`, value: targetAppBuildText(settings.appVersion) },
      { labelKey: `${PREFIX}.fields.testRoomInfoConfigVersion`, value: text(settings.roomInfoConfigVersion) },
      { labelKey: `${PREFIX}.fields.testUsers`, value: testUsersText(settings.testUsers, t) },
    ],
  };
}

/**
 * The Produkte card (#2610089 stage 1): the four product switches as Ja / Nein, and the old product
 * choice ("Produktwahl (alt)") they derive, read-only. When the switches are still derived from the old
 * choice (before the backfill), a grey caption says so.
 */
function productsSection(products: ClientProducts, t: Translate): ClientDetailSection {
  const choice = derivedProductChoice(products);
  const choiceKey = productChoiceLabelKey(choice);
  return {
    titleKey: `${PREFIX}.sections.products`,
    edit: "products",
    ...(products.derived ? { captionKey: "admin.clients.products.derivedCaption" } : {}),
    fields: [
      { labelKey: `${PREFIX}.fields.officeDeviceAssignment`, value: yesNo(products.officeDeviceAssignment, t) },
      { labelKey: `${PREFIX}.fields.project`, value: yesNo(products.project, t) },
      { labelKey: `${PREFIX}.fields.work`, value: yesNo(products.work, t) },
      { labelKey: `${PREFIX}.fields.interface`, value: yesNo(products.interface, t) },
      { labelKey: `${PREFIX}.fields.productChoice`, value: choiceKey ? t(choiceKey) : EMPTY_VALUE },
    ],
  };
}

/**
 * The Projekttypen card (#2610081): each type the client's products allow, Ja when IRES gives it to the
 * client, with whether it is switched on in the client's own table. A member that uses its master's
 * project settings follows the master's list: the card names the master and has no BEARBEITEN. None for
 * a client whose products allow no type.
 */
function projectTypesSection(projectTypes: ClientProjectTypes, group: ClientGroup | undefined, t: Translate): ClientDetailSection | null {
  if (projectTypes.possible.length === 0) {
    return null;
  }
  const followedId = projectTypes.followsMasterClientId;
  const master = followedId !== null && group?.master?.id === followedId ? group.master : null;
  const active = projectTypes.activeInClientTable;
  return {
    titleKey: `${PREFIX}.sections.projectTypes`,
    ...(followedId === null ? { edit: "projectTypes" as const } : {}),
    ...(projectTypes.restricted ? {} : { captionKey: "admin.clients.projectTypes.notRestricted" }),
    fields: [
      ...(followedId !== null
        ? [{
          labelKey: "admin.clients.projectTypes.followsMaster",
          value: master?.name ?? EMPTY_VALUE,
          ...(master ? { clients: [master] } : {}),
        }]
        : []),
      ...projectTypes.possible.map((id) => {
        const allowed = projectTypes.allowed.includes(id);
        return {
          labelKey: `projectDetails.projectTypeDefaultLables.${id}`,
          label: projectTypeText(id, t),
          value: yesNo(allowed, t),
          // Only for a type the client gets and a table that is stored; a withheld type is off anyway.
          ...(allowed && active !== null
            ? { caption: t(`admin.clients.projectTypes.${active.includes(id) ? "activeInClientTable" : "inactiveInClientTable"}`) }
            : {}),
        };
      }),
    ],
  };
}

/**
 * The Projektorganisation card (2026-10-29): the four "... ausführen?" answers the client uses, which
 * IRES Office's own settings page edited until then. A member that uses its master's project settings
 * follows the master's answers: the card names the master and has no BEARBEITEN. None for a client whose
 * products include no projects, as in the guided setup.
 */
function projectOrganisationSection(
  organisation: ClientProjectOrganisation,
  group: ClientGroup | undefined,
  t: Translate
): ClientDetailSection | null {
  if (!organisation.hasProjects) {
    return null;
  }
  const followedId = organisation.followsMasterClientId;
  const master = followedId !== null && group?.master?.id === followedId ? group.master : null;
  return {
    titleKey: `${PREFIX}.sections.projectOrganisation`,
    ...(followedId === null ? { edit: "projectOrganisation" as const } : {}),
    ...(organisation.stored ? {} : { captionKey: "admin.clients.projectOrganisation.notStored" }),
    fields: [
      ...(followedId !== null
        ? [{
          labelKey: "admin.clients.projectOrganisation.followsMaster",
          value: master?.name ?? EMPTY_VALUE,
          ...(master ? { clients: [master] } : {}),
        }]
        : []),
      ...PROJECT_ORGANISATION_QUESTIONS.map((question) => ({
        labelKey: `settings.projectManagement.projectSettings.${question}`,
        value: projectOrganisationText(organisation.value[question], t),
      })),
    ],
  };
}

/**
 * The Systemkonfiguration card (#2610141): every setting of IRES Office's Einstellungen >
 * Systemkonfiguration in its order and wording, Mira last. A member that follows its master's
 * configuration names the master and has no BEARBEITEN, as on the Projektorganisation card.
 */
function systemConfigurationSection(
  configuration: ClientSystemConfiguration,
  group: ClientGroup | undefined,
  t: Translate
): ClientDetailSection {
  const followedId = configuration.followsMasterClientId;
  const master = followedId !== null && group?.master?.id === followedId ? group.master : null;
  return {
    titleKey: `${PREFIX}.sections.systemConfiguration`,
    ...(followedId === null ? { edit: "systemConfiguration" as const } : {}),
    ...(configuration.stored ? {} : { captionKey: "admin.clients.systemConfiguration.notStored" }),
    fields: [
      ...(followedId !== null
        ? [{
          labelKey: "admin.clients.systemConfiguration.followsMaster",
          value: master?.name ?? EMPTY_VALUE,
          ...(master ? { clients: [master] } : {}),
        }]
        : []),
      ...SYSTEM_CONFIGURATION_ROWS.map((row) => ({
        labelKey: row.labelKey,
        value: rowText(row, configuration.value, t),
      })),
    ],
  };
}

/**
 * The Gruppe card (#2610074): MASTER-Mandant or not; a master's members (links) and the member whose
 * settings it took over; a member's master (link). A member's group is changed on its master, so a
 * member's card has no BEARBEITEN; the shared-user migration belongs to a master.
 */
function groupSection(group: ClientGroup, t: Translate): ClientDetailSection {
  const isMember = group.master !== null;
  return {
    titleKey: `${PREFIX}.sections.group`,
    ...(isMember ? {} : { edit: "group" as const }),
    ...(group.isMaster ? { groupAction: "migrateSharedUsers" as const } : {}),
    fields: [
      { labelKey: `${PREFIX}.fields.isMaster`, value: yesNo(group.isMaster, t) },
      ...(group.isMaster
        ? [
          {
            labelKey: `${PREFIX}.fields.groupMembers`,
            value: group.members.length > 0 ? group.members.map((member) => member.name).join(", ") : EMPTY_VALUE,
            clients: group.members,
          },
          { labelKey: `${PREFIX}.fields.settingsSource`, value: group.settingsSource?.name ?? EMPTY_VALUE },
        ]
        : []),
      ...(group.master
        ? [{ labelKey: `${PREFIX}.fields.masterClient`, value: group.master.name, clients: [group.master] }]
        : []),
    ],
  };
}

/**
 * The detail screen's cards, in the IRES Admin Android app's order: master data, contracts, target
 * versions, then the Test card (#2610070, which the app does not have), status, and the counts; the
 * Produkte card (#2610089) and the Gruppe card (#2610074) follow the master data. Pure: the caller
 * passes the translator.
 *
 * @param detail - the overview from `getClientForAdministration`.
 * @param t - translates an i18n key.
 * @param formatCount - formats a count for the current locale (the screen passes `formatNumber`).
 * @param figuresUpdatedAtLabel - when the figures were counted, as shown ("14:07"); null for none.
 * @returns the cards with their labelled values.
 */
export function buildClientDetailSections(
  detail: ClientAdministrationDetail,
  t: Translate,
  formatCount: (value: number) => string,
  figuresUpdatedAtLabel: string | null = null
): ClientDetailSection[] {
  const targetVersionFields = CLIENT_TARGET_VERSION_FIELDS.map(
    (field: ClientTargetVersionField): ClientDetailField => ({
      labelKey: `${PREFIX}.targetVersions.${field}`,
      value: field === "targetAppApk" ? targetAppBuildText(detail.targetVersions?.[field]) : text(detail.targetVersions?.[field]),
      ...(field === "targetRoomInfoConfigVersion" ? { action: "infoConfigTarget" as const } : {}),
    })
  );

  // Only from an IRES Cloud that serves it (#2610081), and only when the products allow a type.
  const projectTypesCard = detail.projectTypes ? projectTypesSection(detail.projectTypes, detail.group, t) : null;
  // Only from an IRES Cloud that serves it (2026-10-29), and only when the products include projects.
  const projectOrganisationCard = detail.projectOrganisation
    ? projectOrganisationSection(detail.projectOrganisation, detail.group, t)
    : null;

  return [
    {
      titleKey: `${PREFIX}.sections.masterData`,
      ...(detail.masterData ? { edit: "masterData" as const } : {}),
      fields: [
        { labelKey: `${PREFIX}.fields.clientNumber`, value: text(detail.clientNumber) },
        { labelKey: `${PREFIX}.fields.companyName`, value: text(detail.companyName) },
        { labelKey: `${PREFIX}.fields.zip`, value: text(detail.zip) },
        // The city is editable, so it is shown too; only an IRES Cloud that serves the Stammdaten has it.
        ...(detail.masterData ? [{ labelKey: `${PREFIX}.fields.city`, value: text(detail.masterData.city) }] : []),
        { labelKey: `${PREFIX}.fields.street`, value: text(detail.street) },
        { labelKey: `${PREFIX}.fields.clientType`, value: clientTypeLabel(detail.clientType, t) },
        { labelKey: `${PREFIX}.fields.email`, value: text(detail.email) },
        { labelKey: `${PREFIX}.fields.clientShorthand`, value: text(detail.iresProjectNumberPrefix) },
      ],
    },
    // Directly after Stammdaten, before Gruppe. Only from an IRES Cloud that serves it (#2610089); an
    // older one leaves the card out, so the card's edit is hidden.
    ...(detail.products ? [productsSection(detail.products, t)] : []),
    // After Produkte, whose switches decide the possible types.
    ...(projectTypesCard ? [projectTypesCard] : []),
    // After Projekttypen: which apps work on those projects.
    ...(projectOrganisationCard ? [projectOrganisationCard] : []),
    // After Projektorganisation (#2610141). Only from an IRES Cloud that serves it.
    ...(detail.systemConfiguration ? [systemConfigurationSection(detail.systemConfiguration, detail.group, t)] : []),
    // Only from an IRES Cloud that serves it (#2610074); an older one leaves the card out.
    ...(detail.group ? [groupSection(detail.group, t)] : []),
    {
      titleKey: `${PREFIX}.sections.contracts`,
      ...(detail.contracts ? { edit: "contracts" as const } : {}),
      fields: [
        { labelKey: `${PREFIX}.fields.contracts`, value: contractsLabel(detail.contractTypes ?? [], t) },
        {
          labelKey: `${PREFIX}.fields.warrantyExtensionForPanels`,
          value: withSince(yesNo(detail.hasWarrantyExtensionForPanels, t), detail.contracts?.hasWarrantyExtensionForPanelsSince, t),
        },
        {
          labelKey: `${PREFIX}.fields.serviceContract`,
          value: withSince(
            yesNo(detail.hasServiceContractForFirmwaresAndConfigs, t),
            detail.contracts?.hasServiceContractForFirmwaresAndConfigsSince,
            t
          ),
        },
      ],
    },
    {
      titleKey: `${PREFIX}.sections.targetVersions`,
      fields: targetVersionFields,
    },
    // Only from an IRES Cloud that serves it (#2610070); an older one leaves the card out.
    ...(detail.testSettings ? [testSettingsSection(detail.testSettings, t)] : []),
    {
      titleKey: `${PREFIX}.sections.status`,
      ...(detail.status ? { statusAction: detail.status.status.deleted ? ("restore" as const) : ("delete" as const) } : {}),
      fields: [
        { labelKey: `${PREFIX}.fields.hasApp`, value: yesNo(detail.hasApp, t) },
        ...statusFields(detail, t),
        {
          labelKey: `${PREFIX}.fields.liveOperation`,
          value: liveOperationValue(detail.liveOperation, t),
          action: "liveOperation" as const,
        },
        // "Mira: Alle Mandanten" (#2610111) on IRES's own accounts only, and wherever it is still on so it
        // can be switched off.
        ...(detail.isIresAccount || detail.miraAllClients?.enabled
          ? [
              {
                labelKey: `${PREFIX}.fields.miraAllClients`,
                value: yesNo(detail.miraAllClients?.enabled ?? false, t),
                action: "miraAllClients" as const,
              },
            ]
          : []),
        { labelKey: `${PREFIX}.fields.numUsers`, value: count(detail.numUsers, formatCount), link: "users" },
        ...projectAndReportFields(detail, formatCount, figuresUpdatedAtLabel),
      ],
    },
    // The Hilfsfunktionen card (#2610077), after Status: one action, "Alle Trocknungen neu berechnen",
    // which the shared IRES admin login does not get (the screen gates it on `mayEdit`).
    {
      titleKey: `${PREFIX}.sections.helperFunctions`,
      descriptionKey: "admin.clients.helperFunctions.recalculateAllDryings.description",
      helperAction: "recalculateAllDryings",
      fields: [],
    },
  ];
}

/** The app bar title: the client's name (or email), plus its client number, as in the Android app. */
export function clientDetailTitle(detail: ClientAdministrationDetail): string {
  const name = detail.clientName?.trim() || detail.email?.trim() || "";
  const number = detail.clientNumber ? ` (${detail.clientNumber})` : "";

  return (name + number).trim();
}

/**
 * The app bar title of a page inside a client: "Mandanten > SanTec (1234) > Geräte". The client's
 * title is left out when it is not known (a sub-page opened by a link or reloaded before its detail
 * was seen), so the page still names itself rather than showing a gap.
 */
export function clientPageTitle(base: string, clientTitle: string | null, ...pages: string[]): string {
  return [base, clientTitle ?? "", ...pages].filter((part) => part.trim() !== "").join(" > ");
}

/** One row of the device counts table: a label and the cells Panels, HyDry, Slim, Edge, ComBoxes, Minis. */
export interface ClientDeviceCountRow {
  labelKey: string;
  panels: string;
  hyDry: string;
  slim: string;
  edge: string;
  comBoxes: string;
  minis: string;
  /** Which row of the card this is, for opening the device list behind one of its numbers. */
  rowKey: DeviceCountAssignmentType | "former" | "total";
  /** The totals row, shown emphasised. */
  isTotal: boolean;
}

/**
 * The device counts table, laid out as the Android detail screen's rows: one per assignment type,
 * then the former panels, then the totals. ComBoxes and Minis have no former count and no panel
 * types, so those cells read "–". Pure.
 *
 * @param counts - the counts from `getClientDeviceCountsForAdministration`.
 * @param formatCount - formats a count for the current locale.
 * @returns the rows.
 */
export function buildClientDeviceCountRows(
  counts: ClientDeviceCounts,
  formatCount: (value: number) => string
): ClientDeviceCountRow[] {
  const typeRows = DEVICE_COUNT_ASSIGNMENT_TYPES.map((type) => {
    const panels = counts.panels.byAssignmentType[type];

    return {
      labelKey: `${PREFIX}.devices.assignmentTypes.${type}`,
      panels: formatCount(panels.total),
      hyDry: formatCount(panels.byPanelType["0"]),
      slim: formatCount(panels.byPanelType["1"]),
      edge: formatCount(panels.byPanelType["2"]),
      comBoxes: formatCount(counts.comBoxes.byAssignmentType[type]),
      minis: formatCount(counts.minis.byAssignmentType[type]),
      rowKey: type,
      isTotal: false,
    };
  });

  return [
    ...typeRows,
    {
      labelKey: `${PREFIX}.devices.former`,
      panels: formatCount(counts.formerPanels.total),
      hyDry: formatCount(counts.formerPanels.byPanelType["0"]),
      slim: formatCount(counts.formerPanels.byPanelType["1"]),
      edge: formatCount(counts.formerPanels.byPanelType["2"]),
      comBoxes: EMPTY_VALUE,
      minis: EMPTY_VALUE,
      rowKey: "former",
      isTotal: false,
    },
    {
      labelKey: `${PREFIX}.devices.total`,
      panels: formatCount(counts.panels.total),
      hyDry: EMPTY_VALUE,
      slim: EMPTY_VALUE,
      edge: EMPTY_VALUE,
      comBoxes: formatCount(counts.comBoxes.total),
      minis: formatCount(counts.minis.total),
      rowKey: "total",
      isTotal: true,
    },
  ];
}

/** One row of the Lizenzen card. */
export interface ClientLicenceRow {
  /** The label, from IRES Office's own licence overview (`assignLicences.*`). */
  labelKey: string;
  /** Model 3: used; model 2: null (its usage is not shown). */
  used: number | null;
  assigned: number;
  /** More used than purchased. */
  exceeded: boolean;
}

const V3_LABELS: Record<string, string> = {
  basic: "assignLicences.basic",
  damageAssessmentAndLeakDetection: "assignLicences.damageAssessmentAndLeakDetection",
  dryingAndRepair: "assignLicences.dryingAndRepair",
};

const V2_LABELS: Record<string, string> = {
  BASIC_LICENCE: "assignLicences.basic",
  DRYING_PROJECT_LICENCE: "assignLicences.drying",
  LEAK_DETECTION_PROJECT_LICENCE: "assignLicences.leakDetection",
};

/**
 * The Lizenzen card's rows, by the rules of IRES Office's own licence overview
 * (`LicenceUsageDetails`): for model 3, Basis always and the other types only when something is used
 * or purchased, flagged when more are used than purchased; for model 2 the three limits, as IRES
 * Admin shows them. Pure.
 */
export function buildClientLicenceRows(licences: ClientLicenceSummary | undefined): ClientLicenceRow[] {
  if (!licences) {
    return [];
  }

  if (licences.model === "V3") {
    return licences.counts
      .filter((count) => count.type === "basic" || count.used + count.assigned > 0)
      .map((count) => ({
        labelKey: V3_LABELS[count.type],
        used: count.used,
        assigned: count.assigned,
        exceeded: count.used > count.assigned,
      }));
  }

  return licences.limits.map((limit) => ({
    labelKey: V2_LABELS[limit.type],
    used: null,
    assigned: limit.assigned,
    exceeded: false,
  }));
}
